import { saveAs } from "file-saver";
import JSZip from "jszip";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchStorageFile } from "../lib/fetchStorageFile";
import { resultFileKey } from "../lib/formatJobResults";
import { runPool } from "../lib/runPool";
import {
  EMPTY_RESULT_DOWNLOAD_PROGRESS,
  type ResultDownloadFile,
  type ResultDownloadProgress,
} from "../model/resultDownload";

const DOWNLOAD_CONCURRENCY = 6;

const zipFileName = (scenarioName: string) => {
  const safe = scenarioName.replace(/[^\w.-]+/g, "-").replace(/^-|-$/g, "") || "rooms";
  return `${safe}-room-dictionaries.zip`;
};

export const useResultZipDownload = () => {
  const { t } = useTranslation();
  const abortRef = useRef<AbortController | null>(null);
  const completedRef = useRef(0);
  const failedRef = useRef(0);
  const partialRef = useRef<Record<string, number>>({});
  const [open, setOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<ResultDownloadProgress>(EMPTY_RESULT_DOWNLOAD_PROGRESS);
  const [error, setError] = useState<null | string>(null);

  const publishDownload = useCallback((total: number) => {
    const inFlight = Object.values(partialRef.current).reduce((sum, value) => sum + value / 100, 0);
    const processed = completedRef.current + failedRef.current;
    const percentage = total === 0 ? 0 : Math.min(100, Math.round(((processed + inFlight) / total) * 100));
    setProgress({
      completed: completedRef.current,
      failed: failedRef.current,
      percentage,
      phase: "download",
      total,
    });
  }, []);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRunning(false);
    setOpen(false);
  }, []);

  const start = useCallback(
    async (selected: ResultDownloadFile[], scenarioName: string, archiveName?: string) => {
      if (selected.length === 0 || isRunning) return;
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      completedRef.current = 0;
      failedRef.current = 0;
      partialRef.current = {};
      setError(null);
      setProgress({ completed: 0, failed: 0, percentage: 0, phase: "download", total: selected.length });
      setOpen(true);
      setIsRunning(true);

      const archive = new JSZip();
      await runPool(
        selected,
        DOWNLOAD_CONCURRENCY,
        async (file) => {
          if (abortController.signal.aborted) return;
          const id = resultFileKey(file.folderName, file.fileName);
          if (!file.path) {
            failedRef.current += 1;
            publishDownload(selected.length);
            return;
          }
          partialRef.current[id] = 0;
          publishDownload(selected.length);
          try {
            const blob = await fetchStorageFile({
              onProgress: (percentage) => {
                partialRef.current[id] = percentage;
                publishDownload(selected.length);
              },
              path: file.path,
              signal: abortController.signal,
            });
            archive.file(`${file.folderName}/${file.fileName}`, blob);
            delete partialRef.current[id];
            completedRef.current += 1;
            publishDownload(selected.length);
          } catch {
            if (abortController.signal.aborted) return;
            delete partialRef.current[id];
            failedRef.current += 1;
            publishDownload(selected.length);
          }
        },
        abortController.signal
      );

      if (abortController.signal.aborted) return;
      if (completedRef.current === 0) {
        setError(t("Execution.transient.jobDetails.downloadModal.failed"));
        setProgress((current) => ({ ...current, percentage: 100, phase: "error" }));
        setIsRunning(false);
        return;
      }

      setProgress((current) => ({ ...current, percentage: 0, phase: "zip" }));
      try {
        const blob = await archive.generateAsync({ type: "blob" }, (meta) => {
          setProgress((current) => ({ ...current, percentage: Math.round(meta.percent), phase: "zip" }));
        });
        if (abortController.signal.aborted) return;
        saveAs(blob, archiveName ?? zipFileName(scenarioName));
        setProgress((current) => ({ ...current, percentage: 100, phase: "done" }));
      } catch {
        if (abortController.signal.aborted) return;
        setError(t("Execution.transient.jobDetails.downloadModal.failedZip"));
        setProgress((current) => ({ ...current, phase: "error" }));
      }
      setIsRunning(false);
    },
    [isRunning, publishDownload, t]
  );

  return { close, error, isRunning, open, progress, start };
};
